import type { ManualAccessorial, TransactionDetail } from '@/api/useFreight/types'
import { AccessorialFormType } from '@/components/Freight/FreightLineItems/components/AddFreightLineItem/types'
import { invalidateTypedQueries, setTypedQueriesData, useApiClient } from '@/hooks/useApiClient'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'

export function useViewAccessorials() {
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  const useAvailableAccessorialsQuery = ({
    orderId,
    actual,
    customerId,
  }: {
    orderId: string
    actual: boolean
    customerId?: string
  }) =>
    apiClient.useQuery('post', '/freight/transaction/order_dispatch/available_accessorials', {
      params: { query: { order_id: orderId, actual, customer_id: customerId } },
    })

  const useTransactionOverviewQuery = ({ orderId }: { orderId: string }) =>
    apiClient.useQuery('post', '/freight/transaction/order_dispatch/freight_type_overview', {
      params: { query: { order_id: orderId } },
    })

  const useOrderOverrideAccessorialMutation = apiClient.useMutation(
    'post',
    '/freight/transaction/order_dispatch/override',
    {
      onSuccess: async (response, request) => {
        setTypedQueriesData(
          queryClient,
          'post',
          '/freight/transaction/order_dispatch/freight_type_overview',
          {},
          (oldData) => {
            if (!oldData) return
            const updatedData = oldData.map((item) => (item.id === response.id ? response : item))
            return updatedData
          }
        )
        invalidateTypedQueries(queryClient, 'post', '/freight/transaction/order_dispatch/freight_type_overview')
      },
      onError: (error) => {
        message.error(error?.detail || 'Failed to override accessorial amount.')
      },
    }
  )

  const useOrderAddManualAccessorialMutation = apiClient.useMutation(
    'post',
    '/freight/transaction/order_dispatch/add_manual_accessorial',
    {
      onSuccess: async (response, request) => {
        setTypedQueriesData(
          queryClient,
          'post',
          '/freight/transaction/order_dispatch/freight_type_overview',
          {},
          (oldData) => {
            if (!oldData) return
            return [...oldData, response]
          }
        )
        invalidateTypedQueries(queryClient, 'post', '/freight/transaction/order_dispatch/freight_type_overview')
      },
      onError: (error) => {
        message.error(error?.detail || 'Failed to add manual accessorial.')
      },
    }
  )

  function overrideMutateAsync(
    formValues: AccessorialFormType,
    accessorial: TransactionDetail,
    isComplete?: boolean,
    customerId?: string
  ) {
    useOrderOverrideAccessorialMutation.mutateAsync({
      body: {
        transaction_id: accessorial.id,
        book_type: accessorial.book_type,
        amount_override: formValues.amount,
        origin_id_override: formValues.origin?.value || null,
        destination_id_override: formValues.destination?.value || null,
        total_override: null,
        actual: isComplete,
        customer_id: customerId,
      },
    })
  }
  function addManualMutateAsync(
    orderId: string,
    accessorial: ManualAccessorial,
    formValues: AccessorialFormType,
    isComplete?: boolean,
    customerId?: string
  ) {
    useOrderAddManualAccessorialMutation.mutateAsync({
      body: {
        order_id: orderId,
        accessorial_id: accessorial._id,
        amount: formValues.amount,
        origin_id: formValues.origin?.value,
        destination_id: formValues.destination?.value,
        book_type: accessorial.book_type,
        actual: isComplete,
        customer_id: customerId,
      },
    })
  }

  return {
    useAvailableAccessorialsQuery,
    useTransactionOverviewQuery,
    useOrderOverrideAccessorialMutation,
    useOrderAddManualAccessorialMutation,
    overrideMutateAsync,
    addManualMutateAsync,
  }
}
